import React, { useMemo, useEffect } from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { ExtendedBooking } from '@wix/ambassador-bookings-reader-v2-extended-booking/types';
import { Member } from '@wix/ambassador-members-ng-api/types';
import {
  ServiceData,
  SlotServices,
  SlotService,
} from '../../../../utils/state/types';
import { FormStatus } from '../../../../types/form-state';
import { BuyPlanButton } from './BuyPlanButton';
import { BookButton } from './BookButton';
import { AddCartButton } from './AddCartButton';
import { useFormComponentContext } from '../../Hooks/useFormComponentContext';
import { CreateBookingErrorType, FormError } from '../../../../types/errors';
import {
  checkDifferentBuyPlans,
  getExceedLimitedPricePlans,
  getSelectedServicesForBuyPlan,
} from '../../../../utils/payment';
import {
  SectionMessage,
  SectionMessageType,
} from '../SectionMessage/SectionMessage';
import { classes } from './ButtonContainer.st.css';
import { getErrorByType } from '../../../../utils/errors/errors';
import { CartFlow } from '../../../../types/types';
import { useFormActions } from '../../Hooks/useFormActions';
import { formatBookingTime } from '../../../../utils/time';
import { mapToArray } from '../../../../utils';

export interface ButtonContainerProps {
  serviceData: ServiceData;
  status: FormStatus;
  selectedNumberOfParticipants?: number;
  errors: FormError[];
  isCart?: boolean;
  isServiceInCart?: boolean;
  existingBookingDetails?: ExtendedBooking;
  memberDetails?: Member;
}

interface NotificationMessageProps {
  slotServices: SlotServices;
  existingBookingDetails?: ExtendedBooking;
  memberDetails?: Member;
}

interface AlertMessageProps {
  slotServices: SlotServices;
  selectedNumberOfParticipants?: number;
  isServiceInCart?: boolean;
}

interface ErrorMessageProps {
  errors: FormError[];
}

const ButtonsContainer: React.FC<ButtonContainerProps> = ({
  serviceData,
  isCart,
  status,
  isServiceInCart,
  errors,
  selectedNumberOfParticipants,
  existingBookingDetails,
  memberDetails,
}) => {
  const { isUpsellPluginInstalled } = useFormComponentContext();

  const slotServicesWithBuyPlans = getSelectedServicesForBuyPlan(
    serviceData.slotServices,
  );
  const exceedLimitedPricePlans = getExceedLimitedPricePlans(
    serviceData.slotServices,
    selectedNumberOfParticipants,
  );
  let buttons;

  if (slotServicesWithBuyPlans.length || exceedLimitedPricePlans.length) {
    buttons = (
      <BuyPlanButton slotServices={serviceData.slotServices} status={status} />
    );
  } else if (isCart) {
    buttons = (
      <>
        <AddCartButton status={status} />
        {!isServiceInCart && !isUpsellPluginInstalled && (
          <BookButton
            slotServices={serviceData.slotServices}
            cartFlow={CartFlow.CHECKOUT}
            status={status}
            primary={false}
          />
        )}
      </>
    );
  } else {
    buttons = (
      <BookButton slotServices={serviceData.slotServices} status={status} />
    );
  }

  return (
    <div className={classes.root}>
      <RenderNotificationMessage
        slotServices={serviceData.slotServices}
        memberDetails={memberDetails}
        existingBookingDetails={existingBookingDetails}
      />
      <RenderAlertMessage
        slotServices={serviceData.slotServices}
        selectedNumberOfParticipants={selectedNumberOfParticipants}
        isServiceInCart={isServiceInCart}
      />
      <RenderErrorMessage errors={errors} />
      <div className={classes.buttons}>{buttons}</div>
    </div>
  );
};

const RenderNotificationMessage: React.FC<NotificationMessageProps> = ({
  slotServices,
  memberDetails,
  existingBookingDetails,
}) => {
  const shouldShowAlreadyBookedAlert = existingBookingDetails && !memberDetails;

  const { t } = useTranslation();
  const { dateRegionalSettingsLocale } = useFormComponentContext();

  let alertText: string = '';

  if (shouldShowAlreadyBookedAlert) {
    const slotService = mapToArray<SlotService>(slotServices)[0];
    const startTime = formatBookingTime(
      slotService,
      existingBookingDetails,
      dateRegionalSettingsLocale,
    );
    const serviceName = slotService.service.name;
    alertText = t('app.bookings-form.booking-details.double-booking-warning', {
      startTime,
      serviceName,
    });
  }

  if (!alertText) {
    return null;
  }

  return (
    <SectionMessage
      type={SectionMessageType.Default}
      text={alertText}
      withoutIcon
    />
  );
};

const RenderAlertMessage: React.FC<AlertMessageProps> = ({
  slotServices,
  selectedNumberOfParticipants,
}) => {
  const { t } = useTranslation();
  const { onConflictAlertShown } = useFormActions();

  let alertText: string = '';

  const isDifferentBuyPlan = checkDifferentBuyPlans(slotServices);
  const exceedPlans = getExceedLimitedPricePlans(
    slotServices,
    selectedNumberOfParticipants,
  );

  if (exceedPlans?.length) {
    const planNames = exceedPlans.map((plan) => plan.label).join(', ');
    alertText = t(
      'app.bookings-form.right-side-alert.not-enough-sessions-left-price-plan',
      { planNames },
    );
  } else if (isDifferentBuyPlan) {
    alertText = t('app.bookings-form.right-side-alert.too-many-plans');
  }

  useEffect(() => {
    if (alertText) {
      onConflictAlertShown(alertText);
    }
  }, [alertText]);

  if (!alertText) {
    return null;
  }

  return (
    <SectionMessage
      type={SectionMessageType.Alert}
      text={alertText}
      withoutIcon
    />
  );
};

const RenderErrorMessage: React.FC<ErrorMessageProps> = ({ errors }) => {
  const { t } = useTranslation();

  const error = getErrorByType({
    errorType: CreateBookingErrorType,
    errors,
  });

  const getErrorMessage = useMemo(() => {
    if (error) {
      switch (error.errorType) {
        case CreateBookingErrorType.SESSION_CAPACITY_EXCEEDED:
        case CreateBookingErrorType.SCHEDULE_CAPACITY_EXCEEDED:
          return t('app.server-errors.not-enough-spots-left');
        case CreateBookingErrorType.VIOLATIONS_WITH_ERROR_SEVERITY:
          return error.params?.message;
        default:
          return t('app.server-errors.slot-not-available');
      }
    }
    return '';
  }, [error]);

  return (
    <SectionMessage type={SectionMessageType.Error} text={getErrorMessage} />
  );
};

export default ButtonsContainer;
